import { useCallback, useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { getPresignedUrl } from '../../common/apiClient';
import './DocumentPdfViewer.css';

const PDFJS_VERSION = pdfjsLib.version || '3.11.174';
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.worker.min.js`;

const MIN_SCALE = 0.5;
const MAX_SCALE = 3;
const SCALE_STEP = 0.15;

function folderKey(s3Key) {
  const idx = (s3Key || '').lastIndexOf('/');
  return idx === -1 ? s3Key : s3Key.substring(0, idx);
}

function isPdf(mimetype, filename) {
  const mt = (mimetype || '').toLowerCase();
  const name = (filename || '').toLowerCase();
  return mt.includes('pdf') || name.endsWith('.pdf');
}

function isImage(mimetype, filename) {
  const mt = (mimetype || '').toLowerCase();
  const name = (filename || '').toLowerCase();
  return mt.startsWith('image/') || /\.(jpe?g|png|webp|gif)$/.test(name);
}

function PdfPage({ pdf, pageNumber, scale }) {
  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const canvas = canvasRef.current;
    if (!pdf || !canvas) return undefined;

    (async () => {
      try {
        const page = await pdf.getPage(pageNumber);
        if (cancelled) return;
        const viewport = page.getViewport({ scale });
        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;

        if (renderTaskRef.current) {
          renderTaskRef.current.cancel();
        }
        const task = page.render({ canvasContext: context, viewport });
        renderTaskRef.current = task;
        await task.promise;
      } catch (err) {
        if (err?.name !== 'RenderingCancelledException') {
          console.error(`Error rendering page ${pageNumber}:`, err);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [pdf, pageNumber, scale]);

  return (
    <div className="dpv-page">
      <canvas ref={canvasRef} className="dpv-page-canvas" />
    </div>
  );
}

export default function DocumentPdfViewer({
  s3Key,
  mimetype,
  filename,
  activeBbox,
  onRegionClick,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [mode, setMode] = useState('pdf');

  const pdfRef = useRef(null);
  const rootRef = useRef(null);
  const loadKeyRef = useRef('');
  const initialFitRef = useRef(false);
  const scrollRef = useRef(null);
  const pagesHostRef = useRef(null);
  const scaleRef = useRef(1);
  const wasFullscreenRef = useRef(false);

  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);

  const computeFitScale = useCallback(async (forFullscreen = fullscreen) => {
    const pdf = pdfRef.current;
    const scrollEl = scrollRef.current;
    if (!pdf) return 1;
    try {
      const page = await pdf.getPage(1);
      const baseViewport = page.getViewport({ scale: 1 });
      let available;
      if (forFullscreen) {
        // En fullscreen mantener zoom similar al panel (~100%), no estirar a todo el monitor
        available = Math.min(window.innerWidth - 48, baseViewport.width * 1.12);
      } else {
        available = (scrollEl?.clientWidth || 400) - 36;
      }
      if (!available || !baseViewport.width) return 1;
      const fit = available / baseViewport.width;
      return Math.min(MAX_SCALE, Math.max(MIN_SCALE, fit));
    } catch {
      return 1;
    }
  }, [fullscreen]);

  const exitFullscreen = useCallback(async () => {
    if (document.fullscreenElement === rootRef.current) {
      try {
        await document.exitFullscreen();
      } catch {
        setFullscreen(false);
      }
    } else {
      setFullscreen(false);
    }
  }, []);

  const toggleFullscreen = useCallback(async () => {
    const el = rootRef.current;
    if (!el) return;
    if (document.fullscreenElement === el) {
      await exitFullscreen();
      return;
    }
    try {
      await el.requestFullscreen();
    } catch {
      setFullscreen(true);
    }
  }, [exitFullscreen]);

  const applyFitWidth = useCallback(async (forFullscreen = fullscreen) => {
    const next = await computeFitScale(forFullscreen);
    setScale(Number(next.toFixed(2)));
  }, [computeFitScale, fullscreen]);

  useEffect(() => {
    if (!s3Key) {
      setError('Sin archivo para previsualizar');
      setLoading(false);
      return undefined;
    }

    const loadKey = `${s3Key}|${mimetype}|${filename}`;
    if (loadKeyRef.current === loadKey) {
      return undefined;
    }
    loadKeyRef.current = loadKey;
    initialFitRef.current = false;

    let cancelled = false;
    pdfRef.current = null;
    setImageUrl(null);
    setNumPages(0);
    setError(null);
    setLoading(true);
    setScale(1);

    (async () => {
      try {
        const presigned = await getPresignedUrl({
          key: folderKey(s3Key),
          contentType: mimetype || 'application/pdf',
          presigned_type: 'get_object',
          file_name: filename,
        });
        if (!presigned?.url) throw new Error('No se pudo obtener URL del documento');

        if (isImage(mimetype, filename)) {
          if (cancelled) return;
          setMode('image');
          setImageUrl(presigned.url);
          setLoading(false);
          return;
        }

        if (!isPdf(mimetype, filename)) {
          throw new Error('Tipo de archivo no soportado para vista previa');
        }

        const response = await fetch(presigned.url);
        if (!response.ok) throw new Error(`Error al descargar (${response.status})`);
        const buffer = await response.arrayBuffer();
        if (cancelled) return;

        const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
        if (cancelled) return;

        pdfRef.current = pdf;
        setMode('pdf');
        setNumPages(pdf.numPages);
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'No se pudo cargar el documento');
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      loadKeyRef.current = '';
    };
  }, [s3Key, mimetype, filename]);

  useEffect(() => {
    if (!numPages || !pdfRef.current || initialFitRef.current) return undefined;
    initialFitRef.current = true;
    const timer = setTimeout(() => {
      applyFitWidth(false);
    }, 80);
    return () => clearTimeout(timer);
  }, [numPages, applyFitWidth]);

  useEffect(() => {
    if (wasFullscreenRef.current && !fullscreen && mode === 'pdf' && numPages) {
      const timer = setTimeout(() => {
        applyFitWidth(false);
      }, 150);
      wasFullscreenRef.current = false;
      return () => clearTimeout(timer);
    }
    if (fullscreen) {
      wasFullscreenRef.current = true;
    }
    return undefined;
  }, [fullscreen, mode, numPages, applyFitWidth]);

  useEffect(() => {
    const onFsChange = () => {
      setFullscreen(document.fullscreenElement === rootRef.current);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  useEffect(() => {
    if (!fullscreen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') exitFullscreen();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [fullscreen, exitFullscreen]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || mode !== 'pdf') return undefined;
    const onWheel = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      setScale((s) => {
        const delta = e.deltaY < 0 ? SCALE_STEP : -SCALE_STEP;
        return Math.min(MAX_SCALE, Math.max(MIN_SCALE, Number((s + delta).toFixed(2))));
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [mode]);

  const zoomIn = () => setScale((s) => Math.min(MAX_SCALE, Number((s + SCALE_STEP).toFixed(2))));
  const zoomOut = () => setScale((s) => Math.max(MIN_SCALE, Number((s - SCALE_STEP).toFixed(2))));

  const handleDownload = async () => {
    try {
      const presigned = await getPresignedUrl({
        key: folderKey(s3Key),
        contentType: mimetype || 'application/pdf',
        presigned_type: 'get_object',
        file_name: filename,
      });
      if (presigned?.url) {
        const a = document.createElement('a');
        a.href = presigned.url;
        a.download = filename || 'documento';
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.click();
      }
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const overlayStyle = activeBbox
    ? {
        left: `${activeBbox.x * 100}%`,
        top: `${activeBbox.y * 100}%`,
        width: `${activeBbox.w * 100}%`,
        height: `${activeBbox.h * 100}%`,
      }
    : null;

  const useFsFallback =
    fullscreen &&
    typeof document !== 'undefined' &&
    document.fullscreenElement !== rootRef.current;

  return (
    <div
      ref={rootRef}
      className={`dpv-root${useFsFallback ? ' dpv-root--fullscreen' : ''}`}
    >
      <div className="dpv-toolbar">
        <div className="dpv-toolbar-start">
          {fullscreen && (
            <button
              type="button"
              className="dpv-btn dpv-btn--back"
              onClick={exitFullscreen}
            >
              ← Volver
            </button>
          )}
          <span className="dpv-toolbar-title">Documento original</span>
        </div>
        <div className="dpv-toolbar-actions">
          {mode === 'pdf' && !loading && !error && (
            <div className="dpv-zoom" aria-label="Zoom">
              <button type="button" className="dpv-btn" onClick={zoomOut} title="Alejar" aria-label="Alejar">
                −
              </button>
              <span className="dpv-zoom-label">{Math.round(scale * 100)}%</span>
              <button type="button" className="dpv-btn" onClick={zoomIn} title="Acercar" aria-label="Acercar">
                +
              </button>
              <button type="button" className="dpv-btn" onClick={() => applyFitWidth(fullscreen)} title="Ajustar al ancho">
                Ajustar
              </button>
            </div>
          )}
          <button type="button" className="dpv-btn" onClick={handleDownload} title="Descargar">
            ↓
          </button>
          <button
            type="button"
            className="dpv-btn"
            onClick={toggleFullscreen}
            title={fullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
          >
            {fullscreen ? '⤡ Salir' : '⤢ Pantalla completa'}
          </button>
        </div>
      </div>

      <div className="dpv-scroll" ref={scrollRef}>
        {loading && (
          <div className="dpv-state">
            <div className="dpv-spinner" />
            <span>Cargando documento…</span>
          </div>
        )}
        {error && !loading && <div className="dpv-state dpv-state--error">{error}</div>}
        {!loading && !error && mode === 'image' && imageUrl && (
          <div className="dpv-image-wrap">
            <img src={imageUrl} alt={filename || 'Documento'} className="dpv-image" />
          </div>
        )}
        {!loading && !error && mode === 'pdf' && pdfRef.current && (
          <div className="dpv-pages" ref={pagesHostRef}>
            {Array.from({ length: numPages }, (_, index) => (
              <PdfPage
                key={`page-${index + 1}`}
                pdf={pdfRef.current}
                pageNumber={index + 1}
                scale={scale}
              />
            ))}
            {overlayStyle && (
              <div
                className="dpv-bbox dpv-bbox--pulse"
                style={overlayStyle}
                onClick={() => onRegionClick?.()}
                role="presentation"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
